<template>
  <div class="app flex flex-col h-screen" :class="{ 'color-picking': appState.isColorPicking }"
    @click="cancelColorPicking">
    <Header class="flex-shrink-0"></Header>
    <div class="main flex flex-row flex-grow">
      <div class="of-hidden flex-shrink-0 flex flex-col">
        <ImageSelector class="overflow-auto flex-grow" />
      </div>
      <ActionDrawer class="flex-shrink-0" />
      <div class="flex-grow relative content items-center"
        :class="{ compare: appState.isCompareMode && appState.currentMenuAction !== 'crop' }">
        <ImageCrop v-if="appState.currentMenuAction === 'crop'" />

        <!-- 非裁剪模式：显示拼接预览 + 编辑区 -->
        <div v-show="appState.currentMenuAction !== 'crop'" class="editor-with-stitch-preview">
          <!-- 上一张拼接预览 -->
          <ImageStitchPreview :key="prevImage ? prevImage.requestId : 'no-prev'" position="prev" :image="prevImage"
            :scale="currentScale" @click="handleSwitchToPrev" :currentEditingWidth="currentEditingWidth" />

          <!-- 当前编辑区域 -->
          <div class="flex image-group current-editing" :class="{ 'canvas-loading': appState.editorLoading }" :style="{
            opacity: appState.editorSize.canvasReady ? 1 : 0,
            height: currentEditingHeight + 'px',
          }">
            <div class="image image-origin" @scroll="onScroll" ref="imageOriginRef">
              <PanZoom :class="{ 'canvas-loading': appState.editorLoading }">
                <img v-if="appState.OriginUrlRemote" :src="getCORSImageUrl(appState.OriginUrlRemote)"
                  style="width: 100%; height: 100%" id="source-img" crossOrigin="anonymous" />
                <div v-else class="abs-center">

                </div>
                <!-- 取消翻译笔刷镜像 -->
                <div class="pointer-canvas" :style="drawingPointerStyleInternal"></div>
              </PanZoom>
            </div>
            <div class="image image-result" @scroll="onScroll" ref="imageResultRef">
              <ImageEditor @drawingPointerStyleChange="v => (drawingPointerStyle = v)" />
            </div>
          </div>

          <!-- 下一张拼接预览 -->
          <ImageStitchPreview :key="nextImage ? nextImage.requestId : 'no-next'" position="next" :image="nextImage"
            :scale="currentScale" @click="handleSwitchToNext" :currentEditingWidth="currentEditingWidth" />
        </div>

        <Loading v-if="!appState.editorSize.canvasReady" class="abs-center"></Loading>

        <!-- AI Assistant 下拉组件 -->
        <AiAssistantDropdown v-if="appState.currentMenuAction !== 'crop'" />
        <EditorBar class="absolute top-[6px] left-[6px]"></EditorBar>

      </div>
      <div class="editor-error" v-if="appState.loadDataError">
        <div class="abs-center">
          <CloseSquareOutlined style="color: red" />
          {{ appState.loadDataError }}
        </div>
      </div>
      <RightSide class="overflow-auto flex-shrink-0"></RightSide>
    </div>
  </div>
  <a-modal v-model:visible="showCookieForbiddenError" cancel-text="取消" ok-text="知道了"
    @ok="showCookieForbiddenError = false">
    <p>您已阻止第三方 Cookie，请在浏览器设置中允许后刷新当前页面</p>
    <p class="text-3">Chrome浏览器: chrome://settings/cookies</p>
  </a-modal>
  <div class="hidden">
    <img :src="getCORSImageUrl(appState.OriginUrl)" id="J-original-img" crossorigin />
    <img :src="appState.RmUrl" id="J-rm-img" crossorigin />
    <canvas id="inpaint-res-canvas"></canvas>
    <canvas id="inpaint-res-canvas-original"></canvas>
  </div>
</template>
<script setup>
import { useStore } from 'vuex';
import Header from '@/components/Header/Header.vue';
import ImageSelector from '@/components/ImageSelector/ImageSelector.vue';
// import Sidebar from '@/components/Sidebar/Sidebar.vue';
// import SidebarDropdown from '@/components/Sidebar/SidebarDropdown.vue';
import ImageEditor from '@/components/ImageEditor/ImageEditor.vue';
import RightSide from '@/components/RightSide.vue';
import EditorBar from '@/components/EditorBar/index.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { CloseSquareOutlined, MenuUnfoldOutlined } from '@ant-design/icons-vue';
import { saveDraft } from '@/utils/ls';
import { sendLog } from '@/utils/sentry';
import { useRoute } from 'vue-router';
import PanZoom from '@/components/PanZoom.vue';
import useSize from '@/components/useSize';
import Loading from '@/components/Loading.vue';
import ActionDrawer from '@/components/ActionDrawer/Index.vue';
import ImageCrop from '@/components/ImageCrop/ImageCrop.vue';
import AiAssistantDropdown from '@/components/AiAssistant/Index.vue';
import ImageStitchPreview from '@/components/ImageStitchPreview/Index.vue';
import { getCORSImageUrl } from '@/utils';
import { getHashValue } from '@/utils';

const theme = getHashValue('theme');
document.body.setAttribute('id', `theme-${theme}`);

const route = useRoute();
const store = useStore();
const appState = computed(() => store.state.app);
const multiState = computed(() => store.state.multi);
const isCompareMode = computed(() => appState.value.isCompareMode);
const editorLoading = computed(() => appState.value.editorLoading);
const { resetEditorSize } = useSize({ appState });

// 当前编辑的图片经过 scale 缩放后的 图片 width
const currentEditingWidth = computed(() => {
  const width = store.getters['app/croppedCanvasWidth'];
  const scale = currentScale.value;
  return Math.round(width * scale);
});

// 图片拼接预览相关
const currentIndex = computed(() => {
  const list = multiState.value.editImageList;
  const currentId = multiState.value.requestId;
  const index = list.findIndex(img => img.requestId === currentId);

  console.log('[currentIndex] 计算:', {
    currentId: currentId,
    index,
    listLength: list.length,
  });

  return index;
});

const prevImage = computed(() => {
  const list = multiState.value.editImageList;
  const index = currentIndex.value;

  if (index > 0 && list.length > 0) {
    const image = list[index - 1];
    const editResult = multiState.value.editResultMap[image.requestId];
    // 优先使用用户编辑后的 url，其次使用 API 翻译后的 apiTranslateUrl，最后使用原图
    const displayUrl = editResult?.url || editResult?.apiTranslateUrl || image.OriginUrl;

    console.log('[prevImage] 计算:', {
      index,
      listLength: list.length,
      requestId: image.requestId.substring(0, 8),
      hasEditResult: !!editResult,
      displayUrl: displayUrl?.substring(0, 80),
    });

    return {
      ...image,
      displayUrl,
    };
  }
  return null;
});

const nextImage = computed(() => {
  const list = multiState.value.editImageList;
  const index = currentIndex.value;

  if (index >= 0 && index < list.length - 1) {
    const image = list[index + 1];
    const editResult = multiState.value.editResultMap[image.requestId];
    // 优先使用用户编辑后的 url，其次使用 API 翻译后的 apiTranslateUrl，最后使用原图
    const displayUrl = editResult?.url || editResult?.apiTranslateUrl || image.OriginUrl;

    console.log('[nextImage] 计算:', {
      index,
      listLength: list.length,
      requestId: image.requestId.substring(0, 8),
      hasEditResult: !!editResult,
      displayUrl: displayUrl?.substring(0, 80),
    });

    return {
      ...image,
      displayUrl,
    };
  }
  return null;
});

const currentScale = computed(() => {
  return appState.value.canvasScale || 1;
});

// 第一步：中间区域高度控制
const currentEditingHeight = computed(() => {
  // const height = appState.value.editorSize.canvasHeight;
  const height = store.getters['app/croppedCanvasHeight'];
  const width = store.getters['app/croppedCanvasWidth'];

  const scale = currentScale.value;
  console.log('[currentEditingHeight] 计算:', { height, width, scaleHeight: Math.round(height * scale), scaleWidth: Math.round(width * scale) });

  if (!height) return 'auto';

  return Math.round(height * scale);
});

const handleSwitchToPrev = () => {
  if (!prevImage.value) return;

  const { imageExtenssion } = appState.value;
  store.dispatch('multi/saveSingleImage', { imageExtenssion })
    .then(() => {
      store.dispatch('app/discardActiveObject');
      store.commit('multi/SET_STATE', {
        requestId: prevImage.value.requestId,
      });
      store.commit('app/SET_DRAWING_MODE', false);
    })
    .catch(() => {
      console.error('Save image failed');
    });
};

const handleSwitchToNext = () => {
  if (!nextImage.value) return;

  const { imageExtenssion } = appState.value;
  store.dispatch('multi/saveSingleImage', { imageExtenssion })
    .then(() => {
      store.dispatch('app/discardActiveObject');
      store.commit('multi/SET_STATE', {
        requestId: nextImage.value.requestId,
      });
      store.commit('app/SET_DRAWING_MODE', false);
    })
    .catch(() => {
      console.error('Save image failed');
    });
};

const drawingPointerStyle = ref({});
const drawingPointerStyleInternal = computed(() => {
  const { canvasScale } = appState.value;
  const { width, height, left, top } = drawingPointerStyle.value;
  return {
    ...drawingPointerStyle.value,
    width: parseFloat(width) * canvasScale + 'px',
    height: parseFloat(height) * canvasScale + 'px',
    left: parseFloat(left) * canvasScale + 'px',
    top: parseFloat(top) * canvasScale + 'px',
  };
});

const cancelColorPicking = () => {
  if (appState.value.isColorPicking) {
    store.commit('app/SET_COLOR_PICKING', false);
    sendLog('colorpicking:cancel');
  }
};

// 抽屉
const onActionDrawerChanged = action => {
  if (action) {
    if (action === 'crop') {
      store.commit('app/RESTORE_CROPINFO_TEMP');
    }
    store.commit('app/SET_CURRENT_MENU_ACTION', action);
    store.commit('app/DISCARD_ACTIVE_OBJECT');
  } else {
    store.commit('app/SET_CURRENT_MENU_ACTION', '');
  }
};

const imageOriginRef = ref(null);
const imageResultRef = ref(null);
const scrollInfo = ref({ x: 0, y: 0 });
const onScroll = e => {
  // console.log(e.target);
  const { scrollLeft: x, scrollTop: y } = e.target;
  scrollInfo.value = { x, y };
};

const syncScroll = () => {
  const { x, y } = scrollInfo.value;
  if (appState.value.isCompareMode) {
    imageOriginRef.value.scrollTo(x, y);
  }
  imageResultRef.value.scrollTo(x, y);
};
watch(scrollInfo, syncScroll);
watch(
  isCompareMode,
  nv => {
    nv &&
      nextTick(() => {
        syncScroll();
        resetEditorSize();
      });
  },
  { immediate: true }
);

/**
 *  监听 PostMessage 传递过来的参数进行初始化
 */
window.addEventListener('message', e => {
  const { name, data } = e.data || {};
  if (name === 'INIT_IMAGES_WITH_URLS') {
    // 新格式：直接传入URLs
    console.log('收到URL列表postMessage:', data);
    store.dispatch('multi/initWithUrls', data.urls || []);
    store.commit('app/SET_RETURN_BASE64', data.returnBase64 || false);
    if (data.userInfo) {
      store.commit('app/SET_USER_INFO', data.userInfo);
    }

  }
  // 旧的逻辑
  if (name === 'XJ_IMAGE_EDITOR_REQUESTIDS') {
    const { requestIds, returnBase64 = false } = e.data;
    console.log('XJ_IMAGE_EDITOR_REQUESTIDS', e);
    getRequestIdsFromPostMsg(requestIds || []);
    store.commit('app/SET_RETURN_BASE64', returnBase64);
  }
  if (name === 'XJ_IMAGE_EDITOR_REQUESTIDS_FROM_CONSOLE') {
    const { imageList, requestId, returnBase64 = false } = e.data;
    console.log('XJ_IMAGE_EDITOR_REQUESTIDS_FROM_CONSOLE', e);
    store.commit('app/SET_RETURN_BASE64', returnBase64);
    getRequestIdsFromConsoleMsg(imageList || [], requestId);
  }

  // 接收用户信息用于文字翻译
  if (name === 'XJ_IMAGE_EDITOR_USERINFO') {
    const { userInfo } = e.data;
    store.commit('app/SET_USER_INFO', userInfo);
  }
});
function getRequestIdsFromPostMsg(requestIds) {
  store.dispatch('multi/setRequestIds', requestIds);
  store.dispatch('multi/getImageList', requestIds.join(','));
}
function getRequestIdsFromConsoleMsg(imageList, requestId) {
  store.dispatch('multi/setFixType', 'requestIdsOrUrl');
  store.dispatch('multi/setRequestIdsOrUrl', { imageList, requestId });
  const { loadedMsgListRequestIds } = store.state.multi;
  store.dispatch(
    'multi/getImageList',
    imageList
      .map(i => i.requestId)
      .filter(i => i && loadedMsgListRequestIds.indexOf(i) === -1)
      .join(',')
  );
}
console.log('import.meta.env.MODE:', import.meta.env.MODE);

// 1. 支持新的URL参数
const { RequestId, RequestIds, ImageUrls } = route.query;

// 2. 新增URL初始化函数
function getUrlsFromParams(urls) {
  store.dispatch('multi/initWithUrls', urls);

  const returnBase64 = import.meta.env.MODE === 'development';
  store.commit('app/SET_RETURN_BASE64', returnBase64);
}

// 3. 修改主初始化逻辑
// 解析图片URLs (新格式)
let imageUrlsFromParams = [];
if (ImageUrls) {
  imageUrlsFromParams = ImageUrls.split(',').filter(url => url.trim());
}

// 解析RequestIds (旧格式，向后兼容)
let requestIdsFromUrl = [];
if (RequestIds) {
  // 多个 ID（逗号分隔）：?RequestIds=id1,id2,id3
  requestIdsFromUrl = RequestIds.split(',').filter(id => id.trim());
} else if (RequestId) {
  // 单个 ID：?RequestId=id1
  requestIdsFromUrl = [RequestId];
}
// 初始化策略
if (imageUrlsFromParams.length > 0) {
  console.log('🎯 从URL参数初始化图片URLs:', imageUrlsFromParams);
  getUrlsFromParams(imageUrlsFromParams);
} else if (requestIdsFromUrl.length > 0) {
  console.log('从 URL 初始化 RequestIds:', requestIdsFromUrl);
  getRequestIdsFromPostMsg(requestIdsFromUrl);

  const returnBase64 = import.meta.env.MODE === 'development';
  store.commit('app/SET_RETURN_BASE64', returnBase64);
} else if (import.meta.env.MODE === 'development') {
  console.log('开发环境：使用硬编码测试数据');
  store.commit('app/SET_RETURN_BASE64', true);
  getUrlsFromParams([
    'https://img.alicdn.com/imgextra/i1/1044149349/O1CN01PkLhen2Ivtu4PdUaT_!!1044149349.jpg',
    'https://img.alicdn.com/imgextra/i1/2207440821122/O1CN01pH4F7R1K9vV9E4GEa_!!2207440821122.jpg',
    'https://img.alicdn.com/imgextra/i1/2207440821122/O1CN01XZDqtX1K9vTxge8PG_!!2207440821122.jpg',
    'https://cbu01.alicdn.com/img/ibank/O1CN01k8W53G1Yu5xKlm7cB_!!1004943118-0-cib.jpg_.webp',
    'https://cbu01.alicdn.com/img/ibank/O1CN01ZAQ1j31Yu5xJAxDeC_!!1004943118-0-cib.jpg_.webp',
    'https://cbu01.alicdn.com/img/ibank/O1CN01vRWy911Yu5vL1x2FV_!!1004943118-0-cib.jpg_.webp',
    'https://cbu01.alicdn.com/img/ibank/O1CN01reeXlS1Yu5ubaTioB_!!1004943118-0-cib.jpg_.webp',
    'https://img.alicdn.com/imgextra/i1/2209459931974/O1CN01PGTiNu1QS8wfwVIKn_!!2209459931974.jpg'

  ]);

  // getRequestIdsFromConsoleMsg([
  //   { requestId: 'f8db54b1cbd165e6' },
  //   { requestId: 'ba4f7045644de87b' },
  //   { requestId: '82816b7e-c713-46d0-8a27-247bbb428f73' },
  //   { requestId: '113d1bb4-46ef-47bf-9679-34531f92b5c9' },
  // ]);
} else {
  console.warn('生产环境：等待 postMessage 传入数据或通过 URL 传入 RequestId(s)');
}

const showCookieForbiddenError = ref(false);
if (!navigator.cookieEnabled || !window.localStorage) {
  showCookieForbiddenError.value = true;
  sendLog('cookieForbidden');
}

window.onbeforeunload = function (e) {
  saveDraft();
};
</script>
<style lang="less" scoped>
.pointer-canvas {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 2px solid #000;
}

.canvas-loading {
  opacity: 0.3;
}

.content {
  padding: 0; // 移除 padding，确保容器高度与 Canvas 一致
  box-shadow: inset 0 0 10px 0px rgba(0, 0, 0, 0.1);
  background: #eef2f7;
  // overflow: hidden;
  overflow: auto;

  &.compare {
    .image {
      width: 50%;
    }

    .image-origin {
      display: block;
    }
  }
}

.editor-with-stitch-preview {
  display: flex;
  flex-direction: column;
  justify-content: center;
  // height: 100%;
  overflow-x: auto; // 宽度超出时可滚动
  overflow-y: scroll !important; // 允许查看完整拼接效果
  background: #CFD2DA;
  gap: 0 !important;
  margin: 0;
  padding: 0;
  line-height: 0;
}

.current-editing {
  flex-shrink: 0;
  overflow: hidden; // 隐藏溢出（隐藏 PanZoom 的 margin-top）
  position: relative;
  display: flex;

  margin: 0;
  padding: 0;
  border: none;
  line-height: 0;
}

.image-group {
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
}

.image {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.image-origin {
  margin: 0;
  padding: 0;
  display: none;

  img {
    pointer-events: none;
    user-select: none;
  }
}

.image-result {
  margin: 0;
  padding: 0;
  height: 100%; // 确保继承父容器高度
  overflow: hidden; // 隐藏 PanZoom 的 margin

  .image-editor {
    height: 100%; // 确保继承父容器高度
    overflow: hidden; // 隐藏 PanZoom 的 margin
  }
}

.color-picking {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    cursor: not-allowed;
  }
}

.app {
  overflow: hidden;
}

.main {
  overflow: hidden;
  user-select: none;
}

.img-wrap {
  position: relative;
}

.txt {
  position: absolute;
}

.origin-img {
  pointer-events: none;
  user-select: none;
}

.editor-error {
  position: absolute;
  left: @left-side-width;
  top: @header-height;
  right: @right-side-width;
  bottom: 0;
  color: #999;
  background: rgba(#fff, 0.7);

  .abs-center {
    padding: 5px 10px;
    border-radius: 3px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 15%);
    background: rgba(#fff, 1);
  }
}
</style>
